import type { Dispatch, SetStateAction } from 'react';
import { defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { IS_NAV_4_COLLAPSE_REMINDER_SPOTLIGHT_DISMISSED } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

// Shared analytics source identifier
export const NAV_4_COLLAPSE_REMINDER_SOURCE = 'nav4CollapseReminderSpotlight';

// Shared i18n messages
export const nav4CollapseReminderI18n = defineMessages({
	heading: {
		id: 'nav4-onboarding.nav4-collapse-reminder-spotlight.heading',
		defaultMessage: 'Want to hide the sidebar?',
		description:
			'Heading for the nav 4 collapse reminder spotlight that appears when user shrinks side nav to min width after seeing the first collapse spotlight',
	},
	body: {
		id: 'nav4-onboarding.nav4-collapse-reminder-spotlight.body',
		defaultMessage:
			'Select the collapse icon in the top left of your screen. You can expand it again at any time.',
		description:
			'Text for the nav 4 collapse reminder spotlight that appears when user shrinks side nav to min width after seeing the first collapse spotlight',
	},
	dismissButton: {
		id: 'nav4-onboarding.nav4-collapse-reminder-spotlight.dismiss',
		defaultMessage: 'OK',
		description:
			'Text on the button to click to indicate you are done viewing the nav 4 collapse reminder spotlight',
	},
});

// Shared hook for handling spotlight dismissal
export const useNav4CollapseReminderDismissal = (
	setIsNav4CollapseReminderSpotlightActive: Dispatch<SetStateAction<boolean>>,
) => {
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const dismissSpotlight = () => {
		// Fire analytics event
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'done',
				source: NAV_4_COLLAPSE_REMINDER_SOURCE,
			},
		}).fire();

		// Hide the spotlight
		setIsNav4CollapseReminderSpotlightActive(false);

		// Mark as dismissed in onboarding state
		setOnboardingState({
			key: IS_NAV_4_COLLAPSE_REMINDER_SPOTLIGHT_DISMISSED,
			value: new Date().toISOString(),
		});
	};

	return { dismissSpotlight };
};
